/**
 * Recording a huddle on the web (CallEngine.startRecording / stopRecording), done by the client of the person who
 * presses Record: the Realtime SFU has no server-side recording, so the call is mixed here — every participant's audio
 * (the same Web Audio sources as the speaking meters) into one MediaStreamDestination, and the stage drawn on a canvas
 * (the shared screen when someone shares, else the cameras in a grid, else a plain card) — and recorded with
 * MediaRecorder (MP4 H.264/AAC where the browser can — Chrome 126+, Safari — so the iOS AVKit player can play it;
 * WebM VP8/Opus otherwise). The file is uploaded like any attachment and posted in the huddle's
 * thread (core HuddleViewModel.finishRecording); everyone sees the recording indicator while it runs.
 */
import type { StreamRegistry } from "./sources.ts";

const TYPES = ["video/mp4;codecs=avc1.42E01E,mp4a.40.2", "video/mp4;codecs=avc1,mp4a.40.2", "video/mp4;codecs=avc1,mp4a", "video/webm;codecs=vp8,opus", "video/webm", "video/mp4"];
const W = 1280;
const H = 720;
const FPS = 15;

export function recordingSupported(): boolean {
  return typeof MediaRecorder !== "undefined" && typeof HTMLCanvasElement !== "undefined" && "captureStream" in HTMLCanvasElement.prototype;
}

export type RecorderSources = {
  ctx: AudioContext;
  /** Every audio source playing in the call right now (mine and the others'). */
  audio(): AudioNode[];
  streams: StreamRegistry;
  /** Source ids: screens first (the stage), then cameras. */
  video(): { screens: string[]; cameras: string[] };
};

export type CallRecording = { stop(): Promise<{ blob: Blob; mime: string } | undefined> };

export function startCallRecording(src: RecorderSources): CallRecording | undefined {
  if (!recordingSupported()) return undefined;
  const mimeType = TYPES.find((t) => MediaRecorder.isTypeSupported(t));
  if (mimeType === undefined) return undefined;
  const dest = src.ctx.createMediaStreamDestination();
  const connected = new Set<AudioNode>();
  const syncAudio = () => {
    const now = new Set(src.audio());
    for (const n of now) {
      if (connected.has(n)) continue;
      n.connect(dest);
      connected.add(n);
    }
    for (const n of connected) {
      if (now.has(n)) continue;
      try {
        n.disconnect(dest);
      } catch {
        // already gone
      }
      connected.delete(n);
    }
  };
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const g = canvas.getContext("2d")!;
  const videos = new Map<string, HTMLVideoElement>();
  const videoFor = (id: string): HTMLVideoElement | undefined => {
    const stream = src.streams.get(id);
    if (stream === undefined) return undefined;
    let el = videos.get(id);
    if (el === undefined || el.srcObject !== stream) {
      el = document.createElement("video");
      el.muted = true;
      el.playsInline = true;
      el.srcObject = stream;
      void el.play().catch(() => undefined);
      videos.set(id, el);
    }
    return el;
  };
  const fit = (el: HTMLVideoElement, x: number, y: number, w: number, h: number) => {
    const vw = el.videoWidth || 16;
    const vh = el.videoHeight || 9;
    const s = Math.min(w / vw, h / vh);
    const dw = vw * s;
    const dh = vh * s;
    g.drawImage(el, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
  };
  const started = Date.now();
  const draw = () => {
    syncAudio();
    g.fillStyle = "#1a1d21";
    g.fillRect(0, 0, W, H);
    const { screens, cameras } = src.video();
    const screen = screens.map(videoFor).find((v) => v !== undefined);
    if (screen !== undefined) {
      fit(screen, 0, 0, W, H);
    } else {
      const cams = cameras.map(videoFor).filter((v): v is HTMLVideoElement => v !== undefined).slice(0, 9);
      if (cams.length === 0) {
        g.fillStyle = "#e8e8e8";
        g.font = "600 36px system-ui, sans-serif";
        g.textAlign = "center";
        const secs = Math.floor((Date.now() - started) / 1000);
        g.fillText(`● ${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, "0")}`, W / 2, H / 2);
      } else {
        const cols = Math.ceil(Math.sqrt(cams.length));
        const rows = Math.ceil(cams.length / cols);
        const cw = W / cols;
        const ch = H / rows;
        cams.forEach((v, i) => fit(v, (i % cols) * cw, Math.floor(i / cols) * ch, cw, ch));
      }
    }
  };
  draw();
  const timer = setInterval(draw, 1000 / FPS);
  const stream = new MediaStream([...canvas.captureStream(FPS).getVideoTracks(), ...dest.stream.getAudioTracks()]);
  const rec = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 1_500_000 });
  const parts: Blob[] = [];
  rec.ondataavailable = (e) => {
    if (e.data.size > 0) parts.push(e.data);
  };
  rec.start(1000);
  const cleanup = () => {
    clearInterval(timer);
    for (const n of connected) {
      try {
        n.disconnect(dest);
      } catch {
        // gone
      }
    }
    for (const el of videos.values()) el.srcObject = null;
    for (const t of stream.getTracks()) t.stop();
  };
  return {
    stop: () =>
      new Promise((resolve) => {
        if (rec.state === "inactive") {
          cleanup();
          resolve(undefined);
          return;
        }
        rec.onstop = () => {
          cleanup();
          const mime = mimeType.split(";")[0]!;
          resolve(parts.length === 0 ? undefined : { blob: new Blob(parts, { type: mime }), mime });
        };
        rec.stop();
      }),
  };
}
